// ---------------------------------------------------------------------------
// V11Layout -- Main Layout with Context Injection Pattern
// ---------------------------------------------------------------------------
// Wraps the entire V11 shell (sidebar, header, content area).
// Uses V11LayoutProvider to enable child pages to inject components
// (e.g., GlobalTimeBar) into the header via `useV11Layout().setHeaderControls`.
// ---------------------------------------------------------------------------

import React, { useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Users, GitCompare, Layers, Radio, Filter } from 'lucide-react';
import { V11ThemeToggle } from '@/components/v11/layout/V11ThemeToggle';
import { V11UserMenu } from '@/components/v11/auth/V11UserMenu';
import { V11LayoutProvider, useV11Layout } from '@/contexts/V11LayoutContext';
import logoIcon from '@/assets/icons/logo_felton.svg';

// ---------------------------------------------------------------------------
// Navigation config
// ---------------------------------------------------------------------------

const FELTON_NAV_ITEMS = [
  {
    path: '/v11/channels',
    label: 'Channels',
    description: 'Manage your social channels',
    icon: Radio,
  },
  {
    path: '/v11/discovery',
    label: 'Discovery',
    description: 'Explore fan data and behaviors',
    icon: Search,
  },
  {
    path: '/v11/funnel',
    label: 'Funnel',
    description: 'Build audiences step-by-step',
    icon: Filter,
  },
  // {
  //   path: '/v11/audience',
  //   label: 'Audience',
  //   description: 'Build and manage segments',
  //   icon: Users,
  // },
  {
    path: '/v11/compare',
    label: 'Compare',
    description: 'Compare segments side by side',
    icon: GitCompare,
  },
  {
    path: '/v11/overlap',
    label: 'Overlap',
    description: 'Discover audience overlap',
    icon: Layers,
  },
];

// ---------------------------------------------------------------------------
// Inner layout (consumes V11LayoutContext)
// ---------------------------------------------------------------------------

interface V11LayoutInnerProps {
  readonly children?: React.ReactNode;
}

const V11LayoutInner: React.FC<V11LayoutInnerProps> = ({ children }) => {
  const location = useLocation();
  const { headerControls } = useV11Layout();

  // Preserve Discovery search params when navigating away
  const discoverySearchRef = useRef<string>('');
  const isOnDiscovery = location.pathname === '/v11/discovery';

  useEffect(() => {
    if (isOnDiscovery) {
      discoverySearchRef.current = location.search;
    }
  }, [isOnDiscovery, location.search]);

  // Build nav link target - restore saved search params for Discovery
  const getNavTo = (path: string) => {
    if (path === '/v11/discovery' && !isOnDiscovery && discoverySearchRef.current) {
      return `${path}${discoverySearchRef.current}`;
    }
    return path;
  };

  return (
    <div className="min-h-screen bg-v11-bg text-v11-text-primary flex overflow-x-hidden">
      {/* Left Sidebar - Fixed */}
      <aside className="hidden lg:flex w-56 flex-col border-r border-v11-border bg-v11-bg-secondary/60 backdrop-blur-sm fixed left-0 top-0 bottom-0 z-30">
        {/* Logo Section */}
        <div className="border-b border-v11-border px-4 h-[57px] flex items-center justify-center">
          <Link to="/v11/felton/explore" className="flex items-center justify-center">
            <img
              src={logoIcon}
              alt="Alley Hooper Logo"
              className="max-h-full w-auto object-contain dark:brightness-0 dark:invert"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <div className="space-y-1">
            {FELTON_NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;

              return (
                <Link
                  key={item.path}
                  to={getNavTo(item.path)}
                  className={`flex items-start gap-3 rounded-lg px-3 py-2.5 text-sm transition ${
                    isActive
                      ? 'bg-blue-500/15 text-v11-text-primary border border-blue-500/30 font-medium'
                      : 'text-v11-text-secondary hover:bg-v11-surface-hover hover:text-v11-text-primary'
                  }`}
                >
                  <Icon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.label}</p>
                    <p className="text-xs text-v11-text-muted truncate">{item.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </nav>

        {/* User Menu - Bottom */}
        <div className="border-t border-v11-border px-3 py-4">
          <V11UserMenu />
        </div>
      </aside>

      {/* Main Content - with margin for left sidebar */}
      <div className="flex-1 flex flex-col lg:ml-56 transition-all duration-300 ease-in-out overflow-x-hidden">
        {/* Header */}
        <header className="z-20 border-b border-v11-border bg-v11-bg-secondary/95 backdrop-blur-sm sticky top-0">
          <div className="px-6 py-3">
            <div className="flex items-center justify-between gap-6">
              {/* Mobile branding - only visible on mobile */}
              <div className="flex items-center gap-2 lg:hidden">
                <img
                  src={logoIcon}
                  alt="Alley Hooper Logo"
                  className="h-8 w-8"
                  style={{
                    filter:
                      'invert(58%) sepia(88%) saturate(2555%) hue-rotate(357deg) brightness(103%) contrast(101%)',
                  }}
                />
                <div>
                  <h1 className="text-sm font-semibold text-v11-text-primary">Alley Hooper</h1>
                  <p className="text-xs text-v11-text-muted">Felton Intelligence</p>
                </div>
              </div>

              {/* Center - Page controls (e.g., GlobalTimeBar) */}
              <div className="flex-1 flex items-center justify-center lg:justify-start" data-testid="header-controls-slot">
                {headerControls}
              </div>

              {/* Right side - Theme Toggle */}
              <div className="flex items-center gap-3">
                <V11ThemeToggle />
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Navigation */}
        <div className="lg:hidden border-b border-v11-border bg-v11-bg-secondary/40 px-6 py-3">
          <nav className="flex gap-2 overflow-x-auto">
            {FELTON_NAV_ITEMS.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={getNavTo(item.path)}
                  className={`inline-flex min-w-[10rem] flex-col rounded-lg px-3 py-2 transition ${
                    isActive
                      ? 'bg-blue-500/15 text-v11-text-primary border border-blue-500/30'
                      : 'bg-v11-surface text-v11-text-secondary border border-v11-border'
                  }`}
                >
                  <span className="text-xs font-semibold">{item.label}</span>
                  <span className="text-xs text-v11-text-muted">{item.description}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden px-6 py-8">
          <div className="w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Public V11Layout (wraps with V11LayoutProvider)
// ---------------------------------------------------------------------------

interface V11LayoutProps {
  children?: React.ReactNode;
}

export const V11Layout: React.FC<V11LayoutProps> = ({ children }) => {
  return (
    <V11LayoutProvider>
      <V11LayoutInner>{children}</V11LayoutInner>
    </V11LayoutProvider>
  );
};
